import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { PanelLeft, type LucideIcon } from "lucide-react";

import { cn } from "@repo/blacksmith-ui/cn";
import { Button } from "@repo/blacksmith-ui/button";

export type HeaderIconButtonSize = "sm" | "md";

const SIZE_CLASS: Record<HeaderIconButtonSize, string> = {
  sm: "size-7",
  md: "size-8",
};

const BASE_CLASS =
  "flex shrink-0 items-center justify-center rounded-lg text-neutral-600 dark:text-neutral-400";

const ICON_CLASS = "size-4";

interface HeaderIconButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  icon: LucideIcon;
  /** `sm` (28px) is the Codesmith pane control; `md` (32px) the mobile one. */
  size?: HeaderIconButtonSize;
  "aria-label": string;
}

/** Flat 16px-glyph icon button for a header row. */
export const HeaderIconButton = forwardRef<
  HTMLButtonElement,
  HeaderIconButtonProps
>(function HeaderIconButton(
  { icon: Icon, size = "sm", className, type = "button", ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      type={type}
      variant="header-icon"
      className={cn(SIZE_CLASS[size], className)}
      {...rest}
    >
      <Icon className={ICON_CLASS} />
    </Button>
  );
});

/** Non-interactive twin of HeaderIconButton for skeletons. */
export function HeaderIconPlaceholder({
  icon: Icon,
  size = "sm",
  className,
}: {
  icon: LucideIcon;
  size?: HeaderIconButtonSize;
  className?: string;
}) {
  return (
    <div className={cn(BASE_CLASS, SIZE_CLASS[size], className)}>
      <Icon className={ICON_CLASS} aria-hidden />
    </div>
  );
}

interface PanelToggleButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "children" | "aria-label"> {
  /** Whether the panel this button controls is currently collapsed. */
  collapsed: boolean;
  size?: HeaderIconButtonSize;
}

/** The ⌥B hint matches the shortcut the session view binds. */
export const PanelToggleButton = forwardRef<
  HTMLButtonElement,
  PanelToggleButtonProps
>(function PanelToggleButton({ collapsed, size, ...rest }, ref) {
  return (
    <HeaderIconButton
      ref={ref}
      icon={PanelLeft}
      size={size}
      aria-label={collapsed ? "Expand sidebar (⌥B)" : "Collapse sidebar (⌥B)"}
      {...rest}
    />
  );
});

export function PanelTogglePlaceholder({
  size,
  className,
}: {
  size?: HeaderIconButtonSize;
  className?: string;
}) {
  return (
    <HeaderIconPlaceholder icon={PanelLeft} size={size} className={className} />
  );
}
